import type { BloodTestAbnormality } from '../api/bloodTest';

/** 基準値（min/max のいずれかまたは両方。未設定の側は H/L 判定しない） */
export type BloodTestReferenceRange = {
  min?: number;
  max?: number;
};

/**
 * 院内基準値。
 * - 上限のみ: CRP, γ-GT, NT-proBNP（超過時 H のみ）
 * - 下限のみ: eGFR（未満時 L のみ）
 * - 判定なし: TSAT
 */
export const BLOOD_TEST_REFERENCE_RANGES: Record<string, BloodTestReferenceRange> = {
  WBC: { min: 3500, max: 9100 },
  CRP: { max: 0.14 },
  Hb: { min: 11.3, max: 15.2 },
  Ht: { min: 33.4, max: 44.9 },
  Plt: { min: 13, max: 36.9 },
  BUN: { min: 8, max: 22 },
  Cr: { min: 0.47, max: 0.79 },
  eGFR: { min: 60 },
  UA: { min: 2.5, max: 7 },
  'LDL-C': { min: 70, max: 139 },
  TG: { min: 50, max: 149 },
  TP: { min: 6.7, max: 8.3 },
  Alb: { min: 3.8, max: 5.2 },
  Na: { min: 136, max: 147 },
  K: { min: 3.6, max: 5 },
  Cl: { min: 98, max: 109 },
  Ca: { min: 8.4, max: 10 },
  'T-Bil': { min: 0.3, max: 1.2 },
  AST: { min: 10, max: 40 },
  ALT: { min: 5, max: 40 },
  'γ-GT': { max: 70 },
  HbA1c: { min: 4.6, max: 6.2 },
  TIBC: { min: 246, max: 410 },
  UIBC: { min: 108, max: 325 },
  Fe: { min: 48, max: 154 },
  フェリチン: { min: 3.6, max: 114 },
  'NT-proBNP': { max: 125 },
  TSH: { min: 0.5, max: 5 },
  FT4: { min: 0.9, max: 1.7 },
  P1NP: { min: 26.4, max: 98.2 },
  'TRACP-5b': { min: 120, max: 590 },
  '25OH-D': { min: 30, max: 100 },
  ALP: { min: 38, max: 113 },
  LD: { min: 124, max: 222 },
  CK: { min: 45, max: 163 },
  補正Ca: { min: 8.4, max: 10 },
};

/** 数値と基準値から L/H を判定する（TSAT 等、基準値未定義の項目は null） */
export function computeAbnormalityFromReference(
  key: string,
  value: number | null
): BloodTestAbnormality {
  if (value === null || !Number.isFinite(value)) return null;

  const range = BLOOD_TEST_REFERENCE_RANGES[key];
  if (!range) return null;

  if (range.min !== undefined && value < range.min) return 'L';
  if (range.max !== undefined && value > range.max) return 'H';
  return null;
}
